
import { formatCurrency, formatDate, getPaymentErrorMessage } from "@/utils";
import {
    Flex,
    Box,
    Text,
    Divider,
    Td,
    TableContainer,
    Table,
    Tbody,
    Thead,
    Tr,
    Th,
} from "@chakra-ui/react";
import { useState } from "react";
import { CheckCircleIcon2, CloseCircleIcon } from "@/styles/icons";
import { useAsyncFunction } from "@/hooks/useAsyncFunction";
import { usePayment } from "@/hooks/usePayment";
import { Tooltip, Drawer, Button } from "barte-design-system";


interface DrawerPaymentInvoiceProps {
    isOpen: boolean;
    onClose: () => void;
    selectedPayments: any[];
}

export function DrawerApprovalPayment({
    isOpen,
    onClose,
    selectedPayments,
}: DrawerPaymentInvoiceProps) {
    const [section, setSection] = useState("choose-payment");
    const [errorMessage, setErrorMessage] = useState<{ title?: string; message: string } | null>(null);
    const { processPaymentsApproval } = usePayment();
    const handleSubmit = async () => {

        try {
            const selectedPaymentsIds = selectedPayments.map((sp) => sp?.transferId);

            if (selectedPaymentsIds.length) {
                await processPaymentsApproval(selectedPaymentsIds);
            }
            setSection("payment-success");
        } catch (error: any) {
            const errorMapping = getPaymentErrorMessage(error);
            setErrorMessage(errorMapping);
            setSection("payment-error");
        }

    }
    const { execute, loading } = useAsyncFunction(handleSubmit);
    const handleClose = () => {
        onClose();
        setSection("choose-payment");
        setErrorMessage(null);
    }
    return (
        <Drawer
            title="Aprovar pagamentos"
            isOpen={isOpen}
            onClose={handleClose}
            footer={
                section === "choose-payment"
                    ? {
                        secondaryButton: { children: "Cancelar", variant: "outline", onClick: handleClose },
                        primaryButton: { children: "Aprovar pagamentos", onClick: execute, loading, disabled: loading },
                    }
                    : { primaryButton: { children: "Fechar", variant: "outline", onClick: handleClose } }
            }
        >
                    <Flex
                        direction="column"
                        bg="white"
                        borderRadius="lg"
                        w="100%"
                    >
                        <>
                            {section === "choose-payment" && (
                                <Flex
                                    direction="column"
                                    gap={4}
                                >
                                    <Box>
                                        {/* Título */}
                                        <Text fontSize="lg" fontWeight="600" mb={1}>
                                            Aprovar contas selecionadas?
                                        </Text>

                                        {/* Subtítulo */}
                                        <Text fontSize="sm" color="gray.600" mb={4}>
                                            Essas contas serão agendadas para pagamento.
                                        </Text>

                                        {/* Tabela */}
                                        <TableContainer>
                                            <Table variant="simple">
                                                <Thead>
                                                    <Tr>
                                                        <Th fontSize="sm" fontWeight="500" color="gray.400" borderColor="gray.100">
                                                            Conta
                                                        </Th>
                                                        <Th fontSize="sm" fontWeight="500" color="gray.400" borderColor="gray.100">
                                                            Pagamento
                                                        </Th>
                                                        <Th fontSize="sm" fontWeight="500" color="gray.400" borderColor="gray.100">
                                                            Valor
                                                        </Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {selectedPayments?.map((item, index) => (
                                                        <Tr key={index}>
                                                            <Td borderColor="gray.100">
                                                                <Tooltip label={item.recipientName} placement="top">
                                                                    <Text maxW="120px" textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap" fontSize="sm" fontWeight="500" color="gray.800">
                                                                        {item.recipientName}
                                                                    </Text>
                                                                </Tooltip>
                                                                <Text fontSize="sm" color="gray.500">
                                                                    {item.costCenter}
                                                                </Text>
                                                            </Td>
                                                            <Td fontSize="sm" borderColor="gray.100">
                                                                {item.scheduledDate ? formatDate(item.scheduledDate) : "--/--/----"}
                                                            </Td>
                                                            <Td fontSize="sm" borderColor="gray.100">
                                                                {formatCurrency({ value: item.amount })}
                                                            </Td>
                                                        </Tr>
                                                    ))}
                                                </Tbody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                </Flex>
                            )}
                            {section === "payment-error" && (
                                <>
                                    <Flex
                                        w="100%"
                                        h="100%"
                                        align="center"
                                        justify="center"
                                        flexDir="column"
                                        gap="4"
                                        p={4}
                                    >
                                        <CloseCircleIcon />
                                        {errorMessage?.title && (
                                            <Text
                                                maxW="268px"
                                                w="100%"
                                                fontSize="20px"
                                                fontWeight="semibold"
                                                textAlign="center"
                                            >
                                                {errorMessage.title}
                                            </Text>
                                        )}
                                        <Text
                                            maxW="268px"
                                            w="100%"
                                            fontSize={errorMessage?.title ? "14px" : "20px"}
                                            textAlign="center"
                                            color={errorMessage?.title ? "gray.600" : "inherit"}
                                        >
                                            {errorMessage?.message || "Houve um erro no pagamento da sua fatura. Tente novamente."}
                                        </Text>
                                    </Flex>
                                    {/* Footer */}
                                    <Flex
                                        justify="right"
                                        mt={6}
                                        gap="2"
                                    >
                                        <Button
                                            variant="outline"
                                            onClick={onClose}
                                        >
                                            Fechar
                                        </Button>
                                    </Flex>
                                </>
                            )}
                            {section === "payment-success" && (
                                <>
                                    <Flex
                                        w="100%"
                                        h="100%"
                                        align="center"
                                        justify="center"
                                        flexDir="column"
                                        gap="4"
                                        p={4}
                                    >
                                        <CheckCircleIcon2 w={8} h={8} />
                                        <Text
                                            maxW="268px"
                                            w="100%"
                                            fontSize="24px"
                                            textAlign="center"
                                        >
                                            Pagamentos em processamento.
                                        </Text>
                                    </Flex>
                                </>
                            )}
                        </>
                    </Flex>
        </Drawer>
    );
}